import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { SortDirection } from '@angular/material/sort';
import { ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize, interval, Subscription } from 'rxjs';

import { CatalogOperationsFacade } from '../../../../Facades/catalog-operations.facade';
import { ApiErrorResponse } from '../../../Shared/Interfaces/response.interface';
import { AuthService } from '../../../Shared/Services/auth.service';
import { MsgService } from '../../../Shared/Services/msg.service';
import { reloadOnCountChange } from '../../../Shared/Services/reload-on-count-change.util';
import { ResourceStateService } from '../../../Shared/Services/resource-state.service';
import {
  ALL_DEMANDS_LIST_ROUTE,
  REPORT_TAB_PERMISSIONS,
} from '../Consts/all-demands-display.const';
import {
  ALL_DEMANDS_DEFAULT_CRITERIA,
  ALL_DEMANDS_DEFAULT_ORDER,
  ALL_DEMANDS_DEFAULT_ORDER_BY,
  ALL_DEMANDS_DEFAULT_PAGE,
  ALL_DEMANDS_DEFAULT_PAGE_SIZE,
  ALL_DEMANDS_SUBJECT_TABS,
} from '../Consts/list-all-demands.const';
import {
  REPORT_EXPORT_KEYS,
  REPORT_EXPORT_POLL_MAX_CONSECUTIVE_ERRORS,
  REPORT_EXPORT_POLL_MS,
} from '../Consts/report-export.const';
import {
  AllDemandsFilterCriteria,
  AllDemandsFilters,
} from '../Interfaces/all-demands-filters.interface';
import { DemandExportRecord, DemandExportResponse } from '../Interfaces/demand-export.interface';
import { ReportDemandListResponse } from '../Interfaces/report-demand-list.interface';
import { ReportFilterOptionsResponse } from '../Interfaces/report-filter-options.interface';
import { ReportPickerOption } from '../Interfaces/report-picker-option.interface';
import { DemandExportService } from '../Services/demand-export.service';
import { DemandReportService } from '../Services/demand-report.service';
import { ReportDemandRow } from '../Types/report-demand-row.type';
import { ReportExportPhase } from '../Types/report-export-phase.type';
import { ReportPickerState } from '../Types/report-picker-state.type';
import { ReportSubjectTab } from '../Types/report-subject-tab.type';
import {
  areAllDemandsFiltersEqual,
  buildAllDemandsQueryParams,
  parseAllDemandsQueryParams,
} from '../Shared/all-demands-query-params.util';
import { buildDemandExportBody } from '../Shared/all-demands-http-params.util';
import { resolveBlobFailureKey } from '../Shared/report-blob-error.util';
import { exportFallbackFilename, saveBlobAsFile } from '../Shared/report-file.util';
import {
  toPersonPickerOptions,
  toSubjectPickerOptions,
} from '../Shared/report-picker-options.util';

@Injectable()
export class AllDemandsFacade {
  private readonly _reports = inject(DemandReportService);
  private readonly _exports = inject(DemandExportService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _msg = inject(MsgService);
  private readonly _router = inject(Router);
  private readonly _translate = inject(TranslateService);
  private readonly _currentUser = toSignal(inject(AuthService).currentUser$, {
    initialValue: null,
  });

  private _active = false;
  private _startSubscription: Subscription | null = null;
  private _pollTimer: Subscription | null = null;
  private _consecutivePollErrors = 0;

  readonly filters = signal<AllDemandsFilters>(this._defaultFilters());
  readonly subjectTab = signal<ReportSubjectTab>('application');

  readonly permittedTabs: Signal<ReportSubjectTab[]> = computed(() => {
    const user = this._currentUser();
    return ALL_DEMANDS_SUBJECT_TABS.filter(
      (tab) => user?.hasPermission(REPORT_TAB_PERMISSIONS[tab]) ?? false,
    );
  });

  readonly activeTab: Signal<ReportSubjectTab | null> = computed(() => {
    const tabs = this.permittedTabs();
    const current = this.subjectTab();
    return tabs.includes(current) ? current : (tabs[0] ?? null);
  });

  readonly demands = httpResource<ReportDemandListResponse>(() => {
    const tab = this.activeTab();
    return tab ? this._reports.list(tab, this.filters()) : undefined;
  });

  readonly rows: Signal<ReportDemandRow[]> = computed(
    () => this._resourceState.valueOf(this.demands)?.data?.data ?? [],
  );

  readonly totalCount: Signal<number> = computed(
    () => this._resourceState.valueOf(this.demands)?.data?.count ?? 0,
  );

  readonly coldLoading: Signal<boolean> = computed(() => this.demands.status() === 'loading');

  readonly loading: Signal<boolean> = computed(() => this.demands.isLoading());

  readonly hasError: Signal<boolean> = computed(() => this.demands.error() !== undefined);

  readonly criteria: Signal<AllDemandsFilterCriteria> = computed(() => {
    const { page, pageSize, orderBy, order, ...criteria } = this.filters();
    return criteria;
  });

  readonly activeFilterCount: Signal<number> = computed(() => {
    const f = this.filters();
    return [
      f.statuses.length > 0,
      f.requestedFrom || f.requestedTo,
      f.updatedFrom || f.updatedTo,
      f.platform,
      f.device.trim(),
      f.requesterId,
      f.subjectId,
      f.approverId,
      f.moderatorId,
      f.decidedById,
    ].filter(Boolean).length;
  });

  readonly hasFilters: Signal<boolean> = computed(() => this.activeFilterCount() > 0);

  readonly hasSearch: Signal<boolean> = computed(() => this.filters().search.length > 0);

  private readonly _optionsWanted = signal(false);

  private readonly _filterOptions = httpResource<ReportFilterOptionsResponse>(() => {
    const tab = this.activeTab();
    return this._optionsWanted() && tab ? this._reports.filterOptions(tab) : undefined;
  });

  private readonly _options = computed(() =>
    this._filterOptions.hasValue() ? this._filterOptions.value().data : null,
  );

  readonly filterOptionsState: Signal<ReportPickerState> = computed(() => {
    if (!this._optionsWanted()) return 'idle';
    if (this._filterOptions.error() !== undefined) return 'error';
    return this._filterOptions.hasValue() ? 'ready' : 'loading';
  });

  readonly requesterOptions: Signal<ReportPickerOption[]> = computed(() =>
    toPersonPickerOptions(this._options()?.requesters ?? []),
  );
  readonly subjectOptions: Signal<ReportPickerOption[]> = computed(() =>
    toSubjectPickerOptions(this._options()?.subjects ?? []),
  );
  readonly approverOptions: Signal<ReportPickerOption[]> = computed(() =>
    toPersonPickerOptions(this._options()?.approvers ?? []),
  );
  readonly moderatorOptions: Signal<ReportPickerOption[]> = computed(() =>
    toPersonPickerOptions(this._options()?.moderators ?? []),
  );

  private readonly _exportStarting = signal(false);
  private readonly _exportRecord = signal<DemandExportRecord | null>(null);
  private readonly _exportErrorKey = signal<string | null>(null);
  private readonly _exportDownloading = signal(false);
  private readonly _pollId = signal<string | null>(null);
  private readonly _pollTick = signal(0);

  private readonly _exportPoll = httpResource<DemandExportResponse>(() => {
    this._pollTick();
    const id = this._pollId();
    return id ? this._exports.show(id) : undefined;
  });

  readonly exportDownloading: Signal<boolean> = this._exportDownloading.asReadonly();

  readonly exportPhase: Signal<ReportExportPhase> = computed(() => {
    if (this._exportErrorKey()) return 'failed';
    if (this._exportStarting()) return 'starting';
    return this._exportRecord()?.status ?? 'idle';
  });

  readonly exportBusy: Signal<boolean> = computed(() => {
    const phase = this.exportPhase();
    return phase === 'starting' || phase === 'pending' || phase === 'processing';
  });

  readonly exportRowCount: Signal<number | null> = computed(
    () => this._exportRecord()?.row_count ?? null,
  );

  readonly exportFailureKey: Signal<string> = computed(
    () =>
      this._exportErrorKey() ??
      this._resolvableKey(this._exportRecord()?.failure_key) ??
      REPORT_EXPORT_KEYS.failed,
  );

  constructor() {
    this._registerCountRefresh();
    this._registerPollSettle();
  }

  private _registerCountRefresh(): void {
    reloadOnCountChange(
      () => {
        const counts = this._catalogOperations.counts();
        return counts
          ? counts.reports_application_demands_open + counts.reports_group_demands_open
          : undefined;
      },
      () => {
        if (this._active) this.reload();
      },
    );
  }

  private _registerPollSettle(): void {
    effect(() => {
      const id = this._pollId();
      const record = this._resourceState.valueOf(this._exportPoll)?.data;
      const error = this._exportPoll.error();
      const resolved = this._exportPoll.status() === 'resolved';
      if (id) untracked(() => this._settlePoll(id, record, error, resolved));
    });
  }

  initialise(params: ParamMap): void {
    this._active = true;
    this._resetExport();
    this._optionsWanted.set(false);
    this.filters.set(this._defaultFilters());
    this.subjectTab.set(this.permittedTabs()[0] ?? 'application');
    this.syncFromQueryParams(params);
    this.reload();
  }

  leave(): void {
    this._active = false;
    this._resetExport();
  }

  reload(): void {
    this.demands.reload();
  }

  syncFromQueryParams(params: ParamMap): boolean {
    const parsed = parseAllDemandsQueryParams(params, this.permittedTabs());
    const changed =
      !areAllDemandsFiltersEqual(this.filters(), parsed.filters) ||
      this.subjectTab() !== parsed.tab;
    if (changed) {
      this.filters.set(parsed.filters);
      this.subjectTab.set(parsed.tab);
    }
    if (parsed.malformed) this._pushFiltersToUrl(true);
    return changed;
  }

  setCriteria(partial: Partial<AllDemandsFilterCriteria>): void {
    this.filters.update((current) => ({
      ...current,
      ...partial,
      page: ALL_DEMANDS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setSearch(term: string): void {
    const search = term.trim();
    if (search !== this.filters().search) this.setCriteria({ search });
  }

  clearFilters(): void {
    this.setCriteria({ ...ALL_DEMANDS_DEFAULT_CRITERIA, search: this.filters().search });
  }

  clearAllCriteria(): void {
    this.setCriteria({ ...ALL_DEMANDS_DEFAULT_CRITERIA });
  }

  setSubjectTab(tab: ReportSubjectTab): void {
    if (tab === this.subjectTab()) return;
    this.subjectTab.set(tab);
    this.filters.update((current) => ({
      ...current,
      // decidedById has no tab-scoped picker, so it stays valid on the other tab
      requesterId: null,
      subjectId: null,
      approverId: null,
      moderatorId: null,
      page: ALL_DEMANDS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  setPage(page: number): void {
    this.filters.update((current) => ({ ...current, page }));
    this._pushFiltersToUrl(false);
  }

  setSort(orderBy: string, order: SortDirection): void {
    this.filters.update((current) => ({
      ...current,
      orderBy: order ? orderBy : ALL_DEMANDS_DEFAULT_ORDER_BY,
      order: order || ALL_DEMANDS_DEFAULT_ORDER,
      page: ALL_DEMANDS_DEFAULT_PAGE,
    }));
    this._pushFiltersToUrl(false);
  }

  requestFilterOptions(): void {
    if (this._optionsWanted()) {
      this._filterOptions.reload();
    } else {
      this._optionsWanted.set(true);
    }
  }

  startExport(scope: ReportSubjectTab): void {
    if (this._exportStarting()) return;
    this._resetExport();
    this._exportStarting.set(true);
    this._startSubscription = this._exports
      .store(buildDemandExportBody(scope, this.criteria()))
      .pipe(finalize(() => this._exportStarting.set(false)))
      .subscribe({
        next: (response) => this._trackExport(response.data),
        error: (error: unknown) => this._exportErrorKey.set(this._resolveStartFailureKey(error)),
      });
  }

  downloadExport(): void {
    const record = this._exportRecord();
    if (!record || record.status !== 'completed' || this._exportDownloading()) return;

    this._exportDownloading.set(true);
    this._exports
      .download(record.id)
      .pipe(finalize(() => this._exportDownloading.set(false)))
      .subscribe({
        next: ({ blob, filename }) =>
          saveBlobAsFile(blob, filename ?? exportFallbackFilename(record.created_at)),
        error: (error: unknown) => void this._announceDownloadFailure(error),
      });
  }

  dismissExport(): void {
    this._resetExport();
  }

  private _defaultFilters(): AllDemandsFilters {
    return {
      ...ALL_DEMANDS_DEFAULT_CRITERIA,
      page: ALL_DEMANDS_DEFAULT_PAGE,
      pageSize: ALL_DEMANDS_DEFAULT_PAGE_SIZE,
      orderBy: ALL_DEMANDS_DEFAULT_ORDER_BY,
      order: ALL_DEMANDS_DEFAULT_ORDER,
    };
  }

  private _pushFiltersToUrl(replaceUrl: boolean): void {
    const queryParams = buildAllDemandsQueryParams(
      this.filters(),
      this.activeTab() ?? 'application',
      this.permittedTabs()[0] ?? 'application',
    );
    void this._router.navigate([ALL_DEMANDS_LIST_ROUTE], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private _trackExport(record: DemandExportRecord): void {
    this._exportRecord.set(record);
    this._beginPolling(record.id);
  }

  private _beginPolling(exportId: string): void {
    this._pollTimer?.unsubscribe();
    this._pollId.set(exportId);
    this._pollTimer = interval(REPORT_EXPORT_POLL_MS).subscribe(() => this._advancePoll());
  }

  private _advancePoll(): void {
    if (!this._exportPoll.isLoading()) this._pollTick.update((tick) => tick + 1);
  }

  private _endPolling(): void {
    this._pollTimer?.unsubscribe();
    this._pollTimer = null;
    this._pollId.set(null);
    this._consecutivePollErrors = 0;
  }

  private _settlePoll(
    exportId: string,
    record: DemandExportRecord | undefined,
    error: unknown,
    resolved: boolean,
  ): void {
    if (error !== undefined) {
      this._settlePollError(error);
      return;
    }
    if (resolved) this._consecutivePollErrors = 0;
    if (!record || record.id !== exportId) return;
    this._exportRecord.set(record);
    if (record.status === 'completed' || record.status === 'failed') this._endPolling();
  }

  private _settlePollError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 404) {
      this._failPolling(REPORT_EXPORT_KEYS.fileGone);
      return;
    }
    this._consecutivePollErrors += 1;
    if (this._consecutivePollErrors >= REPORT_EXPORT_POLL_MAX_CONSECUTIVE_ERRORS) {
      this._failPolling(REPORT_EXPORT_KEYS.failed);
    }
  }

  private _failPolling(key: string): void {
    this._exportErrorKey.set(key);
    this._endPolling();
  }

  private _resetExport(): void {
    this._startSubscription?.unsubscribe();
    this._startSubscription = null;
    this._endPolling();
    this._exportStarting.set(false);
    this._exportRecord.set(null);
    this._exportErrorKey.set(null);
    this._exportDownloading.set(false);
  }

  private _resolvableKey(key: string | null | undefined): string | null {
    return key && this._translate.instant(key) !== key ? key : null;
  }

  private _resolveStartFailureKey(error: unknown): string {
    const body =
      error instanceof HttpErrorResponse ? (error.error as ApiErrorResponse | null) : null;
    return (
      this._resolvableKey(body?.errors?.['scope']?.[0]) ??
      this._resolvableKey(body?.message) ??
      REPORT_EXPORT_KEYS.startFailed
    );
  }

  private async _announceDownloadFailure(error: unknown): Promise<void> {
    const gone = error instanceof HttpErrorResponse && error.status === 404;
    const key = await resolveBlobFailureKey(
      error,
      gone ? REPORT_EXPORT_KEYS.fileGone : REPORT_EXPORT_KEYS.downloadFailed,
      this._translate,
    );
    if (gone) {
      this._exportErrorKey.set(key);
    } else {
      this._msg.showSnackBar(key, { type: 'error' });
    }
  }
}
